import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { ACCESS_RESOURCE_TYPE_LABELS, type AccessResourceType } from '@infras/shared-types';
import { Icon } from '../Icon';

/**
 * "Which one?" for /access/new — an ARIA combobox over the inventory entities of
 * the chosen system (accessRequestEntityOptions), with a typed-name fallback so a
 * resource the scanners have not seen can still be asked for.
 *
 * Two outcomes, mirrored to the parent:
 *   - pick an inventory row → onPick({ entityId, name, … }) — same as ?entityId= prefill;
 *   - type (and optionally confirm) a name → onNameChange(text), picked stays null.
 * With `inventory` false (systems the inventory does not track, or a new member,
 * whom the API refuses entity links) it is a plain text input.
 */

export interface EntityPick {
  entityId: string;
  name: string;
  env?: string | null;
  detail?: string | null;
}

interface EntityOption {
  entityId: string;
  name: string;
  resourceType: string;
  env?: string | null;
  detail?: string | null;
}

export const ENTITY_OPTIONS = gql`
  query AccessRequestEntityOptions($resourceType: String!, $q: String, $limit: Int) {
    accessRequestEntityOptions(resourceType: $resourceType, q: $q, limit: $limit) {
      entityId
      name
      resourceType
      env
      detail
    }
  }
`;

/** Systems the inventory tracks (the API's entity table map). */
export const INVENTORY_TYPES: ReadonlySet<AccessResourceType> = new Set<AccessResourceType>([
  'database',
  'droplet',
  'cluster',
  'repo',
  'secret',
]);

const DEBOUNCE_MS = 200;

/** Type glyph. db/server are drawn here: the shared Icon set has no such shapes. */
export function EntityTypeIcon({ type, size = 16 }: { type: string; size?: number }) {
  if (type === 'repo') return <Icon name="repo" size={size} className="req-ico" />;
  if (type === 'cluster') return <Icon name="cluster" size={size} className="req-ico" />;
  if (type === 'secret') return <Icon name="key" size={size} className="req-ico" />;
  const path: ReactNode =
    type === 'database' ? (
      <>
        <ellipse cx="8" cy="3.8" rx="5" ry="1.9" />
        <path d="M3 3.8v8.4c0 1 2.2 1.9 5 1.9s5-.9 5-1.9V3.8M3 8c0 1 2.2 1.9 5 1.9s5-.9 5-1.9" />
      </>
    ) : type === 'droplet' ? (
      <>
        <rect x="2.2" y="2.4" width="11.6" height="4.6" rx="1.2" />
        <rect x="2.2" y="9" width="11.6" height="4.6" rx="1.2" />
        <path d="M4.6 4.7h.01M4.6 11.3h.01" strokeWidth="2" />
      </>
    ) : null;
  if (!path) return <Icon name="search" size={size} className="req-ico" />;
  return (
    <svg
      className="icon req-ico"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}

/** Env badge — the same classes the rest of the app uses for env tags. */
export function EnvBadge({ env }: { env?: string | null }) {
  if (!env) return null;
  const e = env.toLowerCase();
  const cls = e === 'prod' || e === 'production' ? 'badge prod' : `badge env-${e}`;
  return <span className={cls}>{env}</span>;
}

function highlight(name: string, q: string): ReactNode {
  const needle = q.trim().toLowerCase();
  if (!needle) return name;
  const at = name.toLowerCase().indexOf(needle);
  if (at < 0) return name;
  return (
    <>
      {name.slice(0, at)}
      <mark>{name.slice(at, at + needle.length)}</mark>
      {name.slice(at + needle.length)}
    </>
  );
}

export function EntityPicker({
  id,
  resourceType,
  inventory,
  name,
  onNameChange,
  picked,
  onPick,
  invalid,
  disabled,
  placeholder,
  allowTyped = true,
}: {
  id: string;
  resourceType: AccessResourceType;
  inventory: boolean;
  name: string;
  onNameChange: (name: string) => void;
  picked: EntityPick | null;
  onPick: (pick: EntityPick | null) => void;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Offer the "Not in inventory? Use …" typed-name row. Default true (the access
   * form). The change-request linker passes false: a link must be an inventory row.
   */
  allowTyped?: boolean;
}) {
  const listId = useId();
  const badgeId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [typedCommitted, setTypedCommitted] = useState(false);
  const [debounced, setDebounced] = useState(name);
  const [refocus, setRefocus] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(name), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [name]);

  // A different system means a different inventory: drop the committed typed name.
  useEffect(() => {
    setTypedCommitted(false);
    setOpen(false);
  }, [resourceType]);

  useEffect(() => {
    if (refocus) {
      inputRef.current?.focus();
      setRefocus(false);
    }
  }, [refocus]);

  const { data, loading, error } = useQuery<{
    accessRequestEntityOptions: EntityOption[];
  }>(ENTITY_OPTIONS, {
    variables: { resourceType, q: debounced.trim() || null, limit: 20 },
    skip: !inventory || !open || !!picked || typedCommitted,
  });
  // Results belong to the text in the box only once the debounce has caught up and
  // the fetch for it has landed. Until then they are another query's rows (focus
  // loads the empty-query list; × leaves the old query's) — not shown, not
  // selectable, so a quick Enter commits what was typed instead of a stale row.
  const stale = debounced !== name || loading || !data;
  const options = stale ? [] : (data?.accessRequestEntityOptions ?? []);
  const typed = name.trim();
  const hasFallback = allowTyped && typed.length > 0;
  const total = options.length + (hasFallback ? 1 : 0);
  const fallbackIndex = options.length;
  // While stale, the typed-name row is the only row and it is the active one.
  const current = stale ? (hasFallback ? fallbackIndex : -1) : active;

  // Fresh results: highlight the first row so Enter does the obvious thing.
  useEffect(() => {
    setActive(options.length || hasFallback ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale, data, hasFallback]);

  const optId = (i: number) => (i === fallbackIndex ? `${listId}-typed` : `${listId}-opt-${i}`);

  const choose = (i: number) => {
    if (i < 0 || i >= total) return;
    if (i === fallbackIndex) {
      setTypedCommitted(true);
      onPick(null);
    } else {
      const o = options[i];
      onPick({ entityId: o.entityId, name: o.name, env: o.env, detail: o.detail });
    }
    setOpen(false);
  };

  const clear = () => {
    onPick(null);
    onNameChange('');
    setTypedCommitted(false);
    setRefocus(true);
  };

  // ---- plain text: systems without inventory, or callers who may not link ----
  if (!inventory) {
    return (
      <input
        id={id}
        data-testid="req-entity-picker"
        value={name}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        onChange={(e) => onNameChange(e.target.value)}
      />
    );
  }

  // ---- a chosen value (inventory row, or a confirmed typed name) ----
  if (picked || (typedCommitted && typed)) {
    return (
      <div className="req-combo" data-testid="req-entity-picker">
        <div className={`req-combo__control${invalid ? ' req-combo__control--invalid' : ''}`}>
          <EntityTypeIcon type={resourceType} />
          <span className="req-combo__picked">
            {/* Read-only input keeps the field's <label for> pointing at a focusable control. */}
            <input
              id={id}
              readOnly
              value={picked ? picked.name : typed}
              aria-describedby={badgeId}
              aria-invalid={invalid || undefined}
            />
            {picked ? (
              <span className="tag-badge" id={badgeId}>
                linked to inventory
              </span>
            ) : (
              <span className="tag-badge tag-badge--quiet" id={badgeId}>
                typed name · not in inventory
              </span>
            )}
          </span>
          {!disabled && (
            <button
              type="button"
              className="mini-btn mini-btn--icon"
              title="Clear"
              aria-label="Clear the chosen resource"
              onClick={clear}
            >
              <Icon name="close" size={13} className="req-ico" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (total && !stale) setActive((a) => (a + 1) % total);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (total && !stale) setActive((a) => (a <= 0 ? total - 1 : a - 1));
    } else if (e.key === 'Enter') {
      if (open && current >= 0 && current < total) {
        e.preventDefault();
        choose(current);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const showList = open && !disabled;
  const label = ACCESS_RESOURCE_TYPE_LABELS[resourceType];

  return (
    <div className="req-combo">
      <div
        className={`req-combo__control${showList ? ' req-combo__control--open' : ''}${
          invalid ? ' req-combo__control--invalid' : ''
        }`}
      >
        <Icon name="search" size={16} className="req-ico" />
        <input
          ref={inputRef}
          id={id}
          data-testid="req-entity-picker"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={showList ? listId : undefined}
          aria-activedescendant={
            showList && current >= 0 && current < total ? optId(current) : undefined
          }
          aria-invalid={invalid || undefined}
          autoComplete="off"
          value={name}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            onNameChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      </div>
      {showList && (
        // mousedown is cancelled so the input keeps focus (and blur doesn't close
        // the list before the click lands).
        <div
          className="req-combo__list"
          id={listId}
          role="listbox"
          aria-label={`${label} in inventory`}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="req-combo__group" aria-hidden="true">
            {label} in inventory{options.length ? ` · ${options.length}` : ''}
          </div>
          {options.length === 0 && (
            <div className="req-combo__empty" aria-hidden="true">
              {error
                ? 'Inventory search is unavailable — type the name instead.'
                : stale
                  ? 'Searching…'
                  : typed
                    ? 'No match in the inventory.'
                    : 'Nothing to list yet.'}
            </div>
          )}
          {options.map((o, i) => (
            <div
              key={o.entityId}
              id={optId(i)}
              role="option"
              aria-selected={i === current}
              data-testid="req-entity-option"
              className={`req-combo__opt${i === current ? ' req-combo__opt--active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(i)}
            >
              <EntityTypeIcon type={o.resourceType || resourceType} />
              <span className="req-combo__name">{highlight(o.name, debounced)}</span>
              <EnvBadge env={o.env} />
              {o.detail && <span className="req-combo__sub">{o.detail}</span>}
            </div>
          ))}
          {hasFallback && (
            <div
              id={optId(fallbackIndex)}
              role="option"
              aria-selected={current === fallbackIndex}
              data-testid="req-entity-typed"
              className={`req-combo__foot${current === fallbackIndex ? ' req-combo__opt--active' : ''}`}
              onMouseEnter={() => !stale && setActive(fallbackIndex)}
              onClick={() => choose(fallbackIndex)}
            >
              <Icon name="edit" size={13} className="req-ico" />
              <span>
                Not in inventory? Use <strong>&ldquo;{typed}&rdquo;</strong> as the name
              </span>
              <span className="req-combo__keys" aria-hidden="true">
                <kbd>↑↓</kbd> <kbd>↵</kbd>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
